import React, { useState, useEffect, useCallback } from 'react';
import { Input, Button, Dropdown } from '../../../../../common/ui';
import styles from './styles.module.css';

const UNIT_OPTIONS = [
  { value: 'year', label: 'Year' },
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Day' },
];

const AddPeriodModal = ({ open, setOpen, onSave, editingPeriod = null }) => {
  const [formData, setFormData] = useState({
    period: '',
    unit: 'year',
    daysPerUnit: '',
    name: '',
    description: '',
  });

  useEffect(() => {
    if (editingPeriod) {
      const normalizedUnit = editingPeriod.unit
        ? editingPeriod.unit.toLowerCase()
        : 'year';
      setFormData({
        period: String(editingPeriod.period ?? ''),
        unit: normalizedUnit,
        daysPerUnit: String(editingPeriod.daysPerUnit ?? ''),
        name: editingPeriod.name || '',
        description: editingPeriod.description || '',
      });
    } else {
      setFormData({
        period: '',
        unit: 'year',
        daysPerUnit: '',
        name: '',
        description: '',
      });
    }
  }, [editingPeriod, open]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = () => {
    if (!formData.period || !formData.name) {
      return;
    }
    onSave(formData);
    setOpen(false);
  };

  const handleClose = useCallback(() => {
    setOpen(false);
    setFormData({
      period: '',
      unit: 'year',
      daysPerUnit: '',
      name: '',
      description: '',
    });
  }, [setOpen]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, handleClose]);

  const handlePanelClick = (e) => {
    e.stopPropagation();
  };

  const title = editingPeriod ? 'Update Warranty Period' : 'Add Warranty Period';

  if (!open) return null;

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-label={title}>
      <div
        className={styles.overlay}
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className={styles.panel} onClick={handlePanelClick}>
        <div className={styles.upperDiv}>
          <div className={styles.titleDiv}>
            <h2 className={styles.modalTitle}>{title}</h2>
          </div>
          <div className={styles.modalContent}>
            <div className={styles.periodUnitRow}>
              <Input
                id="period"
                label="Period"
                type="number"
                value={formData.period}
                onInput={handleChange}
                placeholder="e.g., 2"
                disabled={!!editingPeriod}
              />
              <div className={styles.textInputContainer}>
                <label htmlFor="unit" className={styles.textInputLabel}>
                  Unit
                </label>
                <Dropdown
                  id="unit"
                  value={formData.unit}
                  onChange={(value) =>
                    setFormData((prev) => ({ ...prev, unit: value }))
                  }
                  options={UNIT_OPTIONS}
                  placeholder="Select unit"
                  disabled={!!editingPeriod}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <Input
                id="name"
                label="Name"
                type="text"
                value={formData.name}
                onInput={handleChange}
                placeholder="Enter name"
              />
            </div>
            <div className={styles.formRow}>
              <div className={styles.textInputContainer}>
                <label htmlFor="description" className={styles.textInputLabel}>
                  Description
                </label>
                <textarea
                  id="description"
                  className={styles.textarea}
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.lowerDiv}>
          <Button type="button" onClick={handleSave}>
            Save
          </Button>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddPeriodModal;
